import React, { useState, useEffect, useCallback } from 'react';
import { X, Heart, MapPin, Eye, ChevronLeft, ChevronRight, Send, MoreVertical, Trash2 } from 'lucide-react';

import { useTheme } from '../../../contexts/ThemeContext';
import api from '../../../services/api';
import { fixMediaUrl } from '../../../utils/mediaHelpers';
import './StoryViewer.css';


const StoryViewer = ({ stories, initialIndex = 0, onClose, currentUser }) => {
  const { isDark } = useTheme();

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [reaction, setReaction] = useState(null);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [views, setViews] = useState([]);
  const [showViews, setShowViews] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isLoved, setIsLoved] = useState(false);



  const currentStory = stories[currentIndex];
  const STORY_DURATION = currentStory?.media_type === 'video' 
    ? (currentStory?.duration_seconds || 15) * 1000 
    : 5000; // 5s pour photos, durée vidéo pour vidéos

  // Auto-progression
  useEffect(() => {
    if (isPaused || showViews) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          // Story suivante ou fermer
          if (currentIndex < stories.length - 1) {
            setCurrentIndex(currentIndex + 1);
            return 0;
          } else {
            // Defer onClose to avoid React warning
            setTimeout(() => onClose(), 0);
            return 100;
          }
        }
        return prev + (100 / (STORY_DURATION / 100));
      });
    }, 100);

    return () => clearInterval(interval);
  }, [currentIndex, stories.length, isPaused, STORY_DURATION, onClose, showViews]);


  // Reset progress when changing story
  useEffect(() => {
    setProgress(0);
    setReaction(null);
  }, [currentIndex]);

  // Fetch reaction when story changes
  useEffect(() => {
    fetchReaction();
  }, [currentIndex, currentStory?.id]);

  // Record view when story changes
  useEffect(() => {
    recordView();
  }, [currentIndex, currentStory?.id, currentStory?.user_id, currentUser?.id]);

  // Check if story is loved when story changes
  useEffect(() => {
    checkIfLoved();
  }, [currentIndex, currentStory?.id]);

  const checkIfLoved = async () => {
    try {
      const response = await api.get(`/stories/${currentStory.id}/love`);
      if (response.data.success) {
        setIsLoved(response.data.data?.is_loved || false);
      }
    } catch (error) {
      // Not loved = normal
    }
  };

  const handleLove = async () => {
    try {
      if (isLoved) {
        await api.delete(`/stories/${currentStory.id}/love`);
        setIsLoved(false);
      } else {
        await api.post(`/stories/${currentStory.id}/love`);
        setIsLoved(true);
      }
    } catch (error) {
      console.error('Love error:', error);
    }
  };



  const fetchReaction = async () => {
    try {
      const response = await api.get(`/stories/${currentStory.id}/reaction`);
      if (response.data.success && response.data.data) {
        setReaction(response.data.data.reaction_type);
      }
    } catch (error) {
      // Pas de réaction = normal
    }
  };

  const recordView = async () => {
    if (currentStory.user_id === currentUser?.id) return;
    
    try {
      await api.post(`/stories/${currentStory.id}/view`);
    } catch (error) {
      console.error('View recording error:', error);
    }
  };

  const handleReaction = async (emoji) => {
    try {
      if (reaction === emoji) {
        // Supprimer la réaction
        await api.delete(`/stories/${currentStory.id}/react`);
        setReaction(null);
      } else {
        // Ajouter/modifier la réaction
        await api.post(`/stories/${currentStory.id}/react`, {
          reaction_type: emoji,
        });
        setReaction(emoji);
      }
      setShowReactionPicker(false);
    } catch (error) {
      console.error('Reaction error:', error);
    }
  };

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Defer onClose to avoid React warning
      setTimeout(() => onClose(), 0);
    }
  }, [currentIndex, stories.length, onClose]);

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette story définitivement ?')) return;
    
    try {
      await api.delete(`/stories/${currentStory.id}`);
      setShowMenu(false);
      // Defer onClose to avoid React warning
      setTimeout(() => onClose(), 0);
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Erreur lors de la suppression');
    }
  };



  const formatTimeLeft = (expiresAt) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const reactions = ['❤️', '🔥', '😂', '😮', '😢', '👏'];

  if (!currentStory) return null;

  return (
    <div 
      className={`story-viewer-overlay ${isDark ? 'dark' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >

      <div className="story-viewer">
        {/* Progress bars */}
        <div className="story-progress-bars">
          {stories.map((_, index) => (
            <div 
              key={index} 
              className={`progress-bar-container ${index === currentIndex ? 'active' : ''} ${index < currentIndex ? 'completed' : ''}`}
            >
              <div 
                className="progress-bar-fill"
                style={{ 
                  width: index === currentIndex ? `${progress}%` : index < currentIndex ? '100%' : '0%' 
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="story-header">
          <div className="story-user-info">
            <div className="story-avatar-container">
              <img 
                src={fixMediaUrl(currentStory.user?.profile?.photo_url || currentStory.user?.profile?.profile_picture) || '/default-avatar.svg'} 
                alt={currentStory.user?.first_name}
                className="story-avatar"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-avatar.svg';
                }}

              />
            </div>


            <div className="story-meta">
              <span className="story-username">
                {currentStory.user?.first_name} {currentStory.user?.last_name}
              </span>
              <span className="story-time">
                {formatTimeLeft(currentStory.expires_at)} restants
              </span>
            </div>
          </div>
          
          <div className="story-actions">
            {currentStory.user_id === currentUser?.id && (
              <>
                <button 
                  className="views-btn"
                  onClick={() => setShowViews(true)}
                >
                  <Eye size={20} />
                  <span>{currentStory.views_count}</span>
                </button>
                <button 
                  className={`menu-btn ${showMenu ? 'active' : ''}`}
                  onClick={() => setShowMenu(!showMenu)}
                >
                  <MoreVertical size={24} />
                </button>

              </>
            )}
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

        </div>

        {/* Content */}
        <div 
          className="story-content"
          onMouseDown={() => setIsPaused(true)}
          onMouseUp={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          {/* Navigation zones */}
          <div className="nav-zone prev" onClick={handlePrev} />
          <div className="nav-zone next" onClick={handleNext} />

          {currentStory.media_type === 'photo' ? (
            <img 
              src={fixMediaUrl(currentStory.media_file)} 
              alt="Story"
              className="story-media"
            />
          ) : (
            <video 
              src={fixMediaUrl(currentStory.media_file)}
              className="story-media"
              autoPlay
              muted
              playsInline
              onEnded={handleNext}
            />
          )}


          {/* Caption */}
          {currentStory.caption && (
            <div className="story-caption">
              {currentStory.caption}
            </div>
          )}

          {/* Location */}
          {currentStory.location && (
            <div className="story-location">
              <MapPin size={16} />
              <span>{currentStory.location}</span>
            </div>
          )}
        </div>

        {/* Footer - Reactions */}
        <div className="story-footer">
          {currentStory.user_id !== currentUser?.id && (
            <div className="reaction-section">
              {/* Love Button */}
              <button 
                className={`love-btn ${isLoved ? 'loved' : ''}`}
                onClick={handleLove}
              >
                <Heart size={24} fill={isLoved ? '#ff4757' : 'none'} color={isLoved ? '#ff4757' : 'currentColor'} />
                <span>{isLoved ? 'Lové' : 'Lover'}</span>
              </button>

              {showReactionPicker ? (
                <div className="reaction-picker">
                  {reactions.map((emoji) => (
                    <button
                      key={emoji}
                      className={`reaction-btn ${reaction === emoji ? 'selected' : ''}`}
                      onClick={() => handleReaction(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : (
                <button 
                  className="reaction-toggle"
                  onClick={() => setShowReactionPicker(true)}
                >
                  {reaction ? (
                    <span className="current-reaction">{reaction}</span>
                  ) : (
                    <>
                      <span className="reaction-icon">😊</span>
                      <span>Réagir</span>
                    </>
                  )}
                </button>
              )}
              
              <button className="message-btn">
                <Send size={20} />
                <span>Répondre</span>
              </button>
            </div>
          )}


          {/* Story counter */}
          <div className="story-counter">
            {currentIndex + 1} / {stories.length}
          </div>
        </div>
      </div>

      {/* Views Modal */}
      {showViews && (
        <div className="views-modal">
          <div className="views-modal-content">
            <div className="views-header">
              <h4>Vues ({currentStory.views_count})</h4>
              <button onClick={() => setShowViews(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="views-list">
              {/* Liste des vues à charger depuis l'API */}
              <p>Chargement des vues...</p>
            </div>
          </div>
        </div>
      )}

      {/* Menu Modal */}
      {showMenu && (
        <div className="story-menu-modal">
          <div className="story-menu-content">
            <button 
              className="story-menu-item delete"
              onClick={handleDelete}
            >
              <Trash2 size={20} />
              <span>Supprimer la story</span>
            </button>
            <button 
              className="story-menu-item cancel"
              onClick={() => setShowMenu(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>

  );
};

export default StoryViewer;
